import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { MdBookmarkBorder, MdDeleteOutline } from "react-icons/md";
import axios from "../../config/axiosInstance";
import { CRM } from "../../config/apiConfig";

/*
  Visões salvas: o recorte que a pessoa olha toda semana ("minhas PRO sem uso") a um
  clique, em vez de remontar os filtros. Cada pessoa tem as suas.

  Salvar com um nome que já existe substitui os filtros daquela visão.
*/
const SavedViewsMenu = ({ filters, searchTerm, onApply }) => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  const { data: views = [] } = useQuery(
    ["crm", "views"],
    async () => {
      const { data } = await axios.get(CRM.VIEWS);
      return data.data;
    },
    { staleTime: 5 * 60 * 1000 }
  );

  const refresh = () => queryClient.invalidateQueries(["crm", "views"]);
  const fail = (requestError) =>
    setError(
      requestError.response?.data?.message || "Não foi possível salvar a visão."
    );

  const save = useMutation(
    () =>
      axios.post(CRM.VIEWS, {
        name,
        filters: { ...filters, search: searchTerm },
      }),
    {
      onSuccess: () => {
        setName("");
        setError("");
        refresh();
      },
      onError: fail,
    }
  );

  const remove = useMutation((id) => axios.delete(`${CRM.VIEWS}/${id}`), {
    onSuccess: refresh,
    onError: fail,
  });

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((previous) => !previous)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-900 transition hover:bg-slate-100"
      >
        <MdBookmarkBorder size={16} /> Visões
        {views.length ? (
          <span className="text-slate-400">{views.length}</span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute left-0 z-30 mt-1.5 w-72 rounded-xl border border-slate-200 bg-white p-2 shadow-xl lg:left-auto lg:right-0">
          {views.length ? (
            <ul className="mb-2 max-h-60 overflow-y-auto">
              {views.map((view) => (
                <li key={view.id} className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      onApply(view);
                      setOpen(false);
                    }}
                    className="min-w-0 flex-1 truncate rounded-lg px-2 py-1.5 text-left text-sm text-slate-700 transition hover:bg-pink-50 hover:text-pink-700"
                  >
                    {view.name}
                  </button>
                  <button
                    type="button"
                    title={`Apagar a visão ${view.name}`}
                    aria-label={`Apagar a visão ${view.name}`}
                    disabled={remove.isLoading}
                    onClick={() => remove.mutate(view.id)}
                    className="shrink-0 rounded-md p-1 text-slate-300 transition hover:bg-rose-50 hover:text-rose-600"
                  >
                    <MdDeleteOutline size={15} />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mb-2 px-2 py-1 text-xs text-slate-500">
              Monte os filtros e guarde aqui para voltar a eles em um clique.
            </p>
          )}

          <form
            className="flex gap-1 border-t border-slate-100 pt-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (name.trim()) save.mutate();
            }}
          >
            <input
              value={name}
              maxLength={60}
              onChange={(event) => setName(event.target.value)}
              placeholder="Nome para os filtros atuais"
              className="min-w-0 flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-xs outline-none focus:border-pink-300"
            />
            <button
              type="submit"
              disabled={!name.trim() || save.isLoading}
              className="shrink-0 rounded-lg bg-pink-600 px-2.5 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
            >
              Salvar
            </button>
          </form>

          {error ? (
            <p className="mt-1.5 px-1 text-[11px] font-medium text-rose-700">{error}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default SavedViewsMenu;
